import { FC, PropsWithChildren, ReactNode, useState } from 'react';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Row } from 'further-ui/components/Layout';

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'noTopBorder',
})<{ noTopBorder?: boolean }>(({ theme, noTopBorder }) => ({
  padding: 0,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderTop: noTopBorder ? 'none' : undefined,
  borderRadius: '0 !important',
  margin: 0,
  overflow: 'visible',
  '&.Mui-expanded': {
    margin: 0,
  },
  '&::before, &.Mui-expanded::before': {
    display: 'none !important',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp: (prop) =>
    prop !== 'expandable' &&
    prop !== 'noBottomBorder' &&
    prop !== 'noTransition',
})<{ expandable?: boolean; noBottomBorder?: boolean; noTransition?: boolean }>(
  ({ theme, expandable, noBottomBorder, noTransition }) => ({
    padding: theme.spacing(theme.spacingSizes.sm),
    borderBottom: `0px solid ${theme.palette.grey[300]}`,
    transition: noTransition ? 'none' : 'border-bottom 0.3s ease-in-out',
    cursor: expandable ? 'pointer' : 'default',
    '&:hover:not(.Mui-disabled)': {
      cursor: expandable ? 'pointer' : 'default',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginLeft: theme.spacing(theme.spacingSizes.sm),
      '& .MuiSvgIcon-root': {
        color: theme.palette.grey[600],
      },
    },
    '&.Mui-expanded': {
      minHeight: 'unset',
      borderBottom: noBottomBorder
        ? 'none'
        : `1px solid ${theme.palette.grey[300]}`,
    },
    '&.Mui-expanded .MuiAccordionSummary-content': {
      margin: 0,
    },
  }),
);

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: 0,
  overflow: 'visible',
});

type Props = {
  title: string;
  titleVariant?: 'h5' | 'h6';
  total?: string;
  totalVariant?: 'h6' | 'body2';
  expandable?: boolean;
  noTopBorder?: boolean;
  noBottomBorder?: boolean;
  noTransition?: boolean;
  customRow?: ReactNode;
};

const AccountingSection: FC<PropsWithChildren<Props>> = ({
  children,
  title,
  titleVariant = 'h5',
  total,
  totalVariant = 'h6',
  expandable = true,
  noTopBorder = false,
  noBottomBorder = false,
  noTransition = false,
  customRow,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <StyledAccordion
      expanded={expanded}
      noTopBorder={noTopBorder}
      onChange={() => {
        if (expandable) {
          setExpanded(!expanded);
        }
      }}
    >
      <StyledAccordionSummary
        expandable={expandable}
        noBottomBorder={noBottomBorder}
        noTransition={noTransition}
        expandIcon={
          expandable ? (
            <ExpandMoreIcon fontSize="small" color="primary" />
          ) : null
        }
        aria-controls={`section-content-${title}`}
        id={`section-header-${title}`}
      >
        <Row fullWidth centered justify="space-between">
          <Typography variant={titleVariant} color="grey.900" lineHeight="24px">
            {title}
          </Typography>
          {total ? (
            <Typography variant={totalVariant}>{total}</Typography>
          ) : null}
        </Row>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
      {customRow ? customRow : null}
    </StyledAccordion>
  );
};

export default AccountingSection;
